import { userManager } from '@/Core/Managers'

import { RevertResourceCorrespondenceConfig } from '../Configs/ResourceCorrespondenceConfig'

import type { MenuItem } from '@/Core/Contracts/UI/Menu'

/**
 * Maps a menu key to the backend entity that gates it, for keys whose menu
 * identifier differs from the resource key in ResourceCorrespondenceConfig
 * (which is shared with CRUD buttons and must not be repurposed here).
 *
 * Only entries the user can be denied are worth listing; anything not mapped
 * here and not in RevertResourceCorrespondenceConfig stays always-visible.
 */
const MENU_KEY_TO_ENTITY: Record<string, string> = {
    Request: 'TransportRequest',
    'Assignment.WithRequests': 'ExecutorAssignment',
    'Assignment.WithoutRequests': 'ExecutorAssignment',
    'Setting.Index': 'List',
    'Setting.SystemEvents': 'SystemEvent',
    'Setting.ErpExchange': 'ExchangeQueue',
    'Setting.PortalSettings': 'TenantOption',
}

/**
 * Management sections that EDIT a resource: gated by a write permission — NOT by
 * read_collection. A dispatcher holds read_collection on User (needed to
 * populate the selects inside request cards) yet has no write right, so these
 * sections hide while the in-card selects keep working.
 *
 * Read-only journals (Системні події → SystemEvent, Журнал обміну ERP →
 * ExchangeQueue) are intentionally NOT here: they are view screens gated by
 * read_collection like operational sections. A super-admin has only
 * read_collection on SystemEvent, so requiring a write right would wrongly hide
 * the journal from them.
 *
 * Довідники (Setting.Index) is intentionally NOT here either: per the access
 * matrix, viewer / dispatcher / senior dispatcher must see reference books in
 * view mode (BUG-11). The pages degrade to read-only on their own — "Додати"
 * needs the global `create` permission and row edit/delete need per-item
 * `_permissions`, none of which ListRule grants below ROLE_ADMIN.
 */
const MANAGEMENT_MENU_KEYS = new Set<string>([
    'Setting.User',
    'Setting.ManualToken',
    'Setting.PortalSettings',
])

/** Permission keys that mean "read only" — anything else counts as write. */
const READ_ONLY_OPS = new Set<string>(['read', 'read_collection'])

/** Resolves the backend entity a menu key is gated by, if any. */
const resolveEntity = (key: string): string | undefined =>
    MENU_KEY_TO_ENTITY[key] ?? RevertResourceCorrespondenceConfig?.[key]

/** Permission flags the backend granted for an entity, e.g. { create: true }. */
const entityPerms = (entity: string): Record<string, boolean> | undefined => userManager.user?.['permissions']?.[entity]

/** True when the user holds read_collection on the entity. */
const canReadCollection = (entity: string): boolean => entityPerms(entity)?.['read_collection'] === true

/**
 * True when the user holds ANY write permission on the entity (anything beyond
 * read / read_collection: create, update, delete, send_invitation, export,
 * import, …). Robust to per-entity naming differences.
 */
const canManage = (entity: string): boolean => {
    const perms = entityPerms(entity)
    if (!perms) return false
    return Object.entries(perms).some(([op, granted]) => granted && !READ_ONLY_OPS.has(op))
}

/**
 * Filters resolved menu items by the current user's resource permissions.
 *
 * A menu item is kept when:
 *  - its key does NOT map to a backend entity (non-resource items such as
 *    "Головна", "Звіти", or the "Налаштування" group itself) — always visible;
 *  - it is a management section (group "Налаштування") and the user has a write
 *    permission on its entity;
 *  - it is an operational resource section and the user has read_collection.
 *
 * Groups / submenus are filtered recursively; a group with no surviving
 * children is dropped so empty sections never appear.
 *
 * The access source of truth is userManager permissions — the same data the
 * CRUD action buttons read — so the menu and the in-page actions stay
 * consistent. In-card selects load collections via read_collection and are
 * unaffected by this menu-only filtering.
 */
export const FilterMenuByAccessTask = (items: MenuItem[]): MenuItem[] => {
    if (!Array.isArray(items)) return []

    const result: MenuItem[] = []

    for (const item of items) {
        if (!item || typeof item !== 'object') continue

        const children = (item as any).children as MenuItem[] | undefined

        // Group / submenu — keep only if at least one child survives.
        if (Array.isArray(children)) {
            const filteredChildren = FilterMenuByAccessTask(children)
            if (filteredChildren.length > 0) {
                result.push({ ...item, children: filteredChildren } as MenuItem)
            }
            continue
        }

        const key = (item as any).key as string | undefined
        const backendEntity = key ? resolveEntity(key) : undefined

        // Non-resource item (no backend mapping) — always visible.
        if (!key || !backendEntity) {
            result.push(item)
            continue
        }

        // Management section — gated by a write permission.
        // Operational section — gated by read_collection.
        const allowed = MANAGEMENT_MENU_KEYS.has(key) ? canManage(backendEntity) : canReadCollection(backendEntity)

        if (allowed) {
            result.push(item)
        }
    }

    return result
}
